import { SectionWithContent } from '@core';
import { useQueryParamsClear } from '@modules';
import { Box, Grid } from '@themed';
import React, { useMemo, useRef } from 'react';
import { ClosableColorPicker, useColorAtIndex } from '../../colorpicker';
import { InstitutionDropDownWithState } from '../../finnoscore-comparison/InstitutionGroupedDropDown';
import { ExportButton, fillColorStyles, Monochrome } from '../shared/components/base';
import { ChartDropDownHeader } from '../shared/components/base/ChartDropDownControl';
import { ChartWizardStep } from '../shared/components/base/ChartWizardStep';
import { finnoChartStyles } from '../shared/components/base/index.styles';
import { ChartDropDownControlComponent } from '../shared/components/control/ChartDropDownControl';
import { DefaultChartData } from '../shared/helpers';
import { useInstitutionDropDownControl } from '../shared/hooks/useInstitutionDropDownControl';
import { usePeriodDropDownControl } from '../shared/hooks/usePeriodDropDownControl';
import { useTypeDropDownControl } from '../shared/hooks/useTypeDropDownControl';
import { HistoryBubble } from './components/HistoryBubble';

const contentStyles = {
  ...finnoChartStyles.content,
  gridTemplateRows: ['max-content max-content auto 1fr auto auto'],
};

export const HistoryChart = () => {
  const exportChartRef = useRef(null);
  const [colorA, setColorA] = useColorAtIndex(0);
  const [colorB, setColorB] = useColorAtIndex(1);

  const { clearQueryParams } = useQueryParamsClear();

  const { types, setTypeId, typeId, type: fsType, isLoading: isTypeLoading } = useTypeDropDownControl();
  const {
    periods: pastPeriodList,
    period: pastPeriod,
    periodId: pastPeriodId,
    setPeriodId: setPastPeriodId,
    isLoading: isPastPeriodLoading,
  } = usePeriodDropDownControl({ type: fsType, paramKey: 'relativePeriodId' });

  const {
    periods: currentPeriodList,
    period: currentPeriod,
    periodId: currentPeriodId,
    setPeriodId: setCurrentPeriodId,
  } = usePeriodDropDownControl({ type: fsType, paramKey: 'currentPeriodId' });

  const {
    institutions,
    institution,
    institutionSetHandler,
    isLoading: isInstitutionLoading,
  } = useInstitutionDropDownControl({ type: fsType, period: currentPeriod, paramKey: 'institutionId' });

  const { institutions: pastInstitutionList, isLoading: isPastInstitutionLoading } = useInstitutionDropDownControl({
    type: fsType,
    period: pastPeriod,
    paramKey: 'pastInstitutionId',
  });

  const pastInstitution = useMemo(
    () => pastInstitutionList?.find((i) => i.name === institution?.name && i.country?.countryCode === institution?.country?.countryCode),
    [pastInstitutionList, institution]
  );

  function handleTypeChange(selectedTypeId: string) {
    if (typeId !== selectedTypeId) {
      clearQueryParams();
    }
    setTypeId(selectedTypeId);
  }

  return (
    <SectionWithContent sx={finnoChartStyles.container}>
      <Grid sx={contentStyles}>
        <ChartWizardStep id='type' label='Type' isLoading={isTypeLoading}>
          <ChartDropDownControlComponent zIndex={20} list={types} selectedId={typeId} setter={handleTypeChange}>
            <ChartDropDownHeader title={fsType?.text || 'Choose a type'} />
          </ChartDropDownControlComponent>
        </ChartWizardStep>

        {fsType && (
          <ChartWizardStep id='pastPeriod' label='Relative period' isLoading={isPastPeriodLoading}>
            <ChartDropDownControlComponent zIndex={15} list={pastPeriodList} selectedId={pastPeriodId} setter={setPastPeriodId}>
              <ChartDropDownHeader title={pastPeriod?.text || 'Choose a period'}>
                <Box onClick={(e) => e.stopPropagation()}>
                  <ClosableColorPicker defaultColor={colorA} setColorHandler={setColorA} />
                </Box>
              </ChartDropDownHeader>
            </ChartDropDownControlComponent>
          </ChartWizardStep>
        )}

        {fsType && (
          <ChartWizardStep id='currentPeriod' label='Current period' isLoading={isPastPeriodLoading} loading={null}>
            <ChartDropDownControlComponent zIndex={10} list={currentPeriodList} selectedId={currentPeriodId} setter={setCurrentPeriodId}>
              <ChartDropDownHeader title={currentPeriod?.text || 'Choose a period'}>
                <Box onClick={(e) => e.stopPropagation()}>
                  <ClosableColorPicker defaultColor={colorB} setColorHandler={setColorB} />
                </Box>
              </ChartDropDownHeader>
            </ChartDropDownControlComponent>
          </ChartWizardStep>
        )}

        {fsType && pastPeriod && currentPeriod && (
          <ChartWizardStep id='bankSelector' label='Institution' isLoading={isInstitutionLoading || isPastInstitutionLoading}>
            <InstitutionDropDownWithState
              institutions={institutions}
              onSelect={institutionSetHandler}
              selectedInstitution={institution}
              fillComponent={<Monochrome sx={fillColorStyles(colorB)} />}
            />
          </ChartWizardStep>
        )}

        {institution && pastInstitution && (
          <Box id='downloadButton'>
            <ExportButton element={exportChartRef.current} type='chart' name={`history.${institution.name}`} />
          </Box>
        )}

        <Box id='chart' variant={institution && pastInstitution ? undefined : 'styles.sampleChart'}>
          <HistoryBubble
            currentInstitute={institution || DefaultChartData.sampleRelative}
            pastInstitute={pastInstitution || DefaultChartData.sample}
            currentYear={currentPeriod?.text?.split('-')[0]}
            pastYear={pastPeriod?.text?.split('-')[0]}
            ref={exportChartRef}
          />
        </Box>
      </Grid>
    </SectionWithContent>
  );
};
